import { useMemo, useState } from "react";
import clsx from "clsx";
import { heroApi } from "../api/hero";
import { useGameStore } from "../store/useGameStore";
import { resolveSkillDisplay, getSkillIconPath } from "../utils/assets";
import { formatExactNumber } from "../utils/formatNumber";
import { SkillIcon } from "./SkillIcon";
import { AdminAssetPath } from "./AdminAssetPath";
import { ModalBackdrop } from "./ModalBackdrop";
import type { HeroClass, SkillActionType, SkillTreeEntry } from "../types";

interface SkillUpgradeModalProps {
  entry: SkillTreeEntry;
  heroClass: HeroClass;
  heroLevel: number;
  skillPoints: number;
  gold: number;
  branch?: number;
  spCostPerUpgrade?: number;
  onClose: () => void;
  onUpgraded: () => void;
}

function formatDamagePct(mult: number, heroClass: HeroClass, actionType: SkillActionType): string {
  if (actionType === "HEAL" || actionType === "HEAL_AOE") {
    return `${Math.round(mult * 100)}% ${heroClass === "MAGE" ? "MATK" : "PATK"}`;
  }
  if (actionType === "STRIP_BUFFS" || actionType === "STRIP_DEBUFFS" || actionType === "SHIELD") {
    return "—";
  }
  const stat = heroClass === "MAGE" ? "MATK" : "PATK";
  return `${Math.round(mult * 100)}% ${stat}`;
}

const ACTION_LABEL: Record<SkillActionType, string> = {
  ATTACK: "Single target",
  AOE: "Area attack",
  HEAL: "Heal ally",
  HEAL_AOE: "Mass heal",
  STRIP_BUFFS: "Dispel enemy buffs",
  STRIP_DEBUFFS: "Purify allies",
  SHIELD: "Shield",
};

function powerLabel(actionType: SkillActionType): string {
  if (actionType === "HEAL" || actionType === "HEAL_AOE") return "Heal power";
  if (actionType === "STRIP_BUFFS" || actionType === "STRIP_DEBUFFS") return "Effect";
  if (actionType === "SHIELD") return "Shield";
  return "Damage multiplier";
}

function isDamageSkill(actionType: SkillActionType): boolean {
  return actionType === "ATTACK" || actionType === "AOE";
}

export function SkillUpgradeModal({
  entry,
  heroClass,
  heroLevel,
  skillPoints,
  gold,
  branch = 0,
  spCostPerUpgrade = 1,
  onClose,
  onUpgraded,
}: SkillUpgradeModalProps) {
  const { refreshHero, refreshBalance } = useGameStore();
  const [upgrading, setUpgrading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const maxLevel = entry.max_level;
  const currentLevel = entry.current_level;
  const isMax = currentLevel >= maxLevel;
  const nextLevel = currentLevel + 1;
  const currentCfg = currentLevel > 0 ? entry.levels[currentLevel - 1] : null;
  const nextCfg = isMax ? null : entry.levels[currentLevel];
  const title = resolveSkillDisplay(entry.skill_name, { [entry.skill_name]: entry.display_name });

  const canUpgrade = useMemo(() => {
    if (isMax || !nextCfg) return { ok: false, reason: "Max level reached" };
    if (!nextCfg.unlocked) return { ok: false, reason: nextCfg.reason ?? "Locked" };
    if (skillPoints < spCostPerUpgrade) return { ok: false, reason: "Not enough skill points" };
    if (gold < nextCfg.gold_cost) return { ok: false, reason: "Not enough gold" };
    return { ok: true, reason: null };
  }, [isMax, nextCfg, skillPoints, gold, spCostPerUpgrade]);

  const handleUpgrade = async () => {
    if (!canUpgrade.ok) return;
    setUpgrading(true);
    setError(null);
    try {
      await heroApi.upgradeSkill(entry.skill_name, branch);
      await Promise.all([refreshHero(), refreshBalance()]);
      onUpgraded();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upgrade failed");
    } finally {
      setUpgrading(false);
    }
  };

  const description = nextCfg?.description ?? currentCfg?.description;

  return (
    <ModalBackdrop
      onClose={onClose}
      className="z-50 items-end md:items-center overflow-y-auto bg-black/60 backdrop-blur-sm"
    >
      <div className="w-full max-w-sm max-h-[85vh] overflow-y-auto bg-kingdom-purple border border-kingdom-light rounded-2xl shadow-2xl md:my-auto">
        <div className="sticky top-0 z-10 bg-kingdom-purple border-b border-kingdom-mid/50 p-4 pb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="space-y-1 shrink-0">
              <SkillIcon
                heroClass={heroClass}
                skill={entry.skill_name}
                className="w-12 h-12"
                alt={title}
              />
              <AdminAssetPath path={getSkillIconPath(heroClass, entry.skill_name)} className="max-w-[3rem]" />
            </div>
            <div className="min-w-0">
              <div className="font-bold text-white truncate">{title}</div>
              <div className="text-xs text-gray-400">Level {currentLevel}/{maxLevel}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="shrink-0 text-gray-400 hover:text-white text-xl leading-none p-1"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        <div className="p-4 pt-3 space-y-4">
        {description && (
          <div className="bg-kingdom-dark rounded-xl p-3 text-sm text-gray-300 leading-relaxed">
            <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Description</div>
            {description}
          </div>
        )}

        {!isMax && nextCfg ? (
          <>
            <div className="bg-kingdom-dark rounded-xl p-3 text-center">
              <div className="text-xs text-gray-400 mb-1">{powerLabel(nextCfg.action_type)}</div>
              {isDamageSkill(nextCfg.action_type) ? (
                currentCfg ? (
                  <>
                    <div className="text-lg font-bold text-gold-300">
                      {formatDamagePct(currentCfg.damage_mult, heroClass, currentCfg.action_type)}
                      <span className="text-gray-500 mx-2">→</span>
                      {formatDamagePct(nextCfg.damage_mult, heroClass, nextCfg.action_type)}
                    </div>
                    <div className="text-xs text-emerald-400 mt-1">
                      +{Math.round((nextCfg.damage_mult - currentCfg.damage_mult) * 100)}% bonus
                    </div>
                  </>
                ) : (
                  <div className="text-lg font-bold text-gold-300">
                    {formatDamagePct(nextCfg.damage_mult, heroClass, nextCfg.action_type)}
                  </div>
                )
              ) : (
                <div className="text-sm font-medium text-kingdom-glow">
                  {ACTION_LABEL[nextCfg.action_type]}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="bg-kingdom-dark rounded-lg px-3 py-2">
                <div className="text-gray-400 text-xs">Mana cost</div>
                <div className="font-medium text-blue-300">{nextCfg.mana_cost}</div>
              </div>
              <div className="bg-kingdom-dark rounded-lg px-3 py-2">
                <div className="text-gray-400 text-xs">Mana gain</div>
                <div className="font-medium text-emerald-300">+{nextCfg.mana_gain}</div>
              </div>
              {(nextCfg.cooldown_sec ?? 0) > 0 && (
                <div className="col-span-2 bg-kingdom-dark rounded-lg px-3 py-2">
                  <div className="text-gray-400 text-xs">Cooldown</div>
                  <div className="font-medium text-amber-300">{nextCfg.cooldown_sec} sec</div>
                </div>
              )}
              <div className="col-span-2 bg-kingdom-dark rounded-lg px-3 py-2">
                <div className="text-gray-400 text-xs">Type</div>
                <div className="font-medium text-kingdom-glow">{ACTION_LABEL[nextCfg.action_type]}</div>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <RequirementRow
                label="Hero level"
                met={heroLevel >= nextCfg.required_hero_level}
                value={`${heroLevel} / ${nextCfg.required_hero_level}`}
              />
              <RequirementRow
                label="Skill points"
                met={skillPoints >= spCostPerUpgrade}
                value={`${skillPoints} / ${spCostPerUpgrade}`}
              />
              <RequirementRow
                label="Gold"
                met={gold >= nextCfg.gold_cost}
                value={`${formatExactNumber(gold)} / ${formatExactNumber(nextCfg.gold_cost)}`}
              />
              {nextCfg.reason && !nextCfg.unlocked && (
                <div className="text-rose-400 text-xs bg-rose-900/20 border border-rose-500/30 rounded-lg p-2">
                  {nextCfg.reason}
                </div>
              )}
            </div>

            {error && (
              <div className="text-rose-400 text-xs bg-rose-900/20 border border-rose-500/30 rounded-lg p-2">
                {error}
              </div>
            )}

            <button
              onClick={handleUpgrade}
              disabled={!canUpgrade.ok || upgrading}
              className={clsx(
                "w-full py-3 rounded-xl font-bold transition-all",
                canUpgrade.ok && !upgrading
                  ? "bg-gradient-to-r from-kingdom-accent to-kingdom-glow text-white"
                  : "bg-gray-800 text-gray-500 cursor-not-allowed",
              )}
            >
              {upgrading
                ? "Upgrading..."
                : canUpgrade.ok
                  ? `Upgrade to Lv.${nextLevel}`
                  : canUpgrade.reason}
            </button>
          </>
        ) : (
          <div className="space-y-3">
            <div className="text-center text-gold-300 font-bold py-2">Max level reached!</div>
            {currentCfg && (currentCfg.cooldown_sec ?? 0) > 0 && (
              <div className="bg-kingdom-dark rounded-lg px-3 py-2 text-sm text-center">
                <div className="text-gray-400 text-xs">Cooldown</div>
                <div className="font-medium text-amber-300">{currentCfg.cooldown_sec} sec</div>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </ModalBackdrop>
  );
}

function RequirementRow({
  label,
  met,
  value,
}: {
  label: string;
  met: boolean;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between bg-kingdom-dark rounded-lg px-3 py-2">
      <span className="text-gray-400">{label}</span>
      <span className={clsx("font-medium tabular-nums", met ? "text-emerald-400" : "text-rose-400")}>
        {met ? "✓" : "✗"} {value}
      </span>
    </div>
  );
}
